import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  ToolCallRow,
  isToolCall,
  isToolResult,
  findResultFor,
  hasCallFor,
  ArtifactBadge,
} from '../ToolCallRow.jsx';
import {
  ApprovalPrompt,
  isApprovalRequested,
  isApprovalResolved,
  approvalRequestID,
  findResolvedFor,
} from '../ApprovalPrompt.jsx';
import { EntityChip } from './EntityChip.jsx';
import { Citation } from './Citation.jsx';
import { URN_REGEX } from '../ChatAttachments.jsx';
import { eventType } from '../lib/events.js';
import { MSG_DISALLOWED } from '../lib/markdown.js';
import { TypingDots } from './TypingDots.jsx';
import { useStore } from '../lib/store.js';
import { V2MessageActions } from './V2MessageActions.jsx';
import { PonderedBody } from './PonderedBody.jsx';
import { Avatar } from './Avatar.jsx';
import { ForwardPopover } from './ForwardPopover.jsx';

const ARTIFACT_BADGE_TAG = 'jobe-artifact-badge';
const ENTITY_CHIP_TAG = 'jobe-entity-chip';
const CITATION_TAG = 'jobe-citation';

// URN prefix sets that route to Citation vs EntityChip.
function isCitationUrn(urn) {
  return urn.startsWith('urn:jobe:memory:') || urn.startsWith('urn:jobe:episode:');
}

function rehypeArtifactBadges() {
  const matchAll = (value) => {
    const re = new RegExp(URN_REGEX.source, 'g');
    const out = [];
    let last = 0;
    let m;
    while ((m = re.exec(value)) !== null) {
      if (m.index > last) {
        out.push({ type: 'text', value: value.slice(last, m.index) });
      }
      out.push({
        type: 'element',
        tagName: ARTIFACT_BADGE_TAG,
        properties: { urn: m[0] },
        children: [],
      });
      last = m.index + m[0].length;
    }
    if (last < value.length) {
      out.push({ type: 'text', value: value.slice(last) });
    }
    return out;
  };
  const visit = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    const next = [];
    let changed = false;
    for (const child of node.children) {
      const has = (s) => new RegExp(URN_REGEX.source).test(s);
      if (child && child.type === 'text' && typeof child.value === 'string' && has(child.value)) {
        const replacement = matchAll(child.value);
        if (replacement.length > 1 || (replacement[0] && replacement[0].type === 'element')) {
          next.push(...replacement);
          changed = true;
          continue;
        }
      }
      if (child && child.type === 'element') visit(child);
      next.push(child);
    }
    if (changed) node.children = next;
  };
  return (tree) => visit(tree);
}

// Matches any urn:jobe:* URN (broader than artifact URNs).
const JOBE_URN_RE = /urn:jobe:[a-z]+:[^\s"'<>]+/g;

function rehypeEntityChips() {
  const matchAll = (value) => {
    const re = new RegExp(JOBE_URN_RE.source, 'g');
    const out = [];
    let last = 0;
    let m;
    while ((m = re.exec(value)) !== null) {
      if (m.index > last) {
        out.push({ type: 'text', value: value.slice(last, m.index) });
      }
      const urn = m[0];
      const tagName = isCitationUrn(urn) ? CITATION_TAG : ENTITY_CHIP_TAG;
      out.push({
        type: 'element',
        tagName,
        properties: { urn },
        children: [],
      });
      last = m.index + m[0].length;
    }
    if (last < value.length) {
      out.push({ type: 'text', value: value.slice(last) });
    }
    return out;
  };
  const visit = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    const next = [];
    let changed = false;
    for (const child of node.children) {
      const has = (s) => new RegExp(JOBE_URN_RE.source).test(s);
      if (child && child.type === 'text' && typeof child.value === 'string' && has(child.value)) {
        const replacement = matchAll(child.value);
        if (replacement.length > 1 || (replacement[0] && replacement[0].type === 'element')) {
          next.push(...replacement);
          changed = true;
          continue;
        }
      }
      if (child && child.type === 'element') visit(child);
      next.push(child);
    }
    if (changed) node.children = next;
  };
  return (tree) => visit(tree);
}

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeArtifactBadges, rehypeEntityChips];

function MessageBody({ text, streaming = false }) {
  if (!text && !streaming) return null;
  return (
    <>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        disallowedElements={MSG_DISALLOWED}
        unwrapDisallowed
        components={{
          a({ href, children, node: _node, ...rest }) {
            const safe = typeof href === 'string' && /^https?:\/\//i.test(href);
            if (!safe) return <span>{children}</span>;
            return (
              <a href={href} {...rest} target="_blank" rel="noreferrer noopener">
                {children}
              </a>
            );
          },
          [ARTIFACT_BADGE_TAG]: ({ node, urn, children: _children, ...rest }) => {
            const u = urn || (node && node.properties && node.properties.urn);
            if (!u) return null;
            return <ArtifactBadge urn={u} {...rest} />;
          },
          [ENTITY_CHIP_TAG]: ({ node, urn, children: _children, ...rest }) => {
            const u = urn || (node && node.properties && node.properties.urn);
            if (!u) return null;
            return <EntityChip urn={u} {...rest} />;
          },
          [CITATION_TAG]: ({ node, urn, children: _children, ...rest }) => {
            const u = urn || (node && node.properties && node.properties.urn);
            if (!u) return null;
            return <Citation urn={u} {...rest} />;
          },
        }}
      >
        {text || ''}
      </ReactMarkdown>
      {streaming && (
        <span className="streaming-caret" aria-label="streaming" aria-live="polite">▍</span>
      )}
    </>
  );
}

function autoBranchTitle(ev) {
  const body = ev?.body || ev?.payload || {};
  const text = (typeof body.content === 'string' && body.content)
    || (typeof body.text === 'string' && body.text) || '';
  return text.split(/[.!?]/)[0]?.trim().slice(0, 60) || 'Branch';
}

function MessageRow({ ev, text, role, msgID, threadURN, channelID, currentBranchId }) {
  const [showForward, setShowForward] = useState(false);
  const isPinned = useStore(s => s.annotationsByThread[threadURN]?.pinned?.has(msgID) ?? false);
  const isPondered = useStore(s => s.annotationsByThread[threadURN]?.pondered?.has(msgID) ?? false);
  const addBranch = useStore(s => s.addBranch);
  const selectBranch = useStore(s => s.selectBranch);
  const body = ev?.body || ev?.payload || {};
  const author = body.author || body.from || body.role || ev?.actor_id || '?';

  function handleBranch() {
    const id = addBranch({
      channelID,
      fromThreadURN: threadURN,
      fromMsgID: msgID,
      title: autoBranchTitle(ev),
      parentBranchId: currentBranchId,
    });
    if (id) selectBranch({ channelID, threadURN, branchId: id });
  }

  const displayName = role === 'user' ? 'You' : author.charAt(0).toUpperCase() + author.slice(1);

  const bubble = (
    <div
      className={`v2-row v2-row-message v2-row-${role} ${isPinned ? 'v2-row-pinned' : ''}`}
      data-testid="v2-row-message"
      data-msg-id={msgID}
    >
      <div className="v2-bubble-header">
        <span className="v2-bubble-author">{displayName}</span>
        <V2MessageActions
          ev={ev}
          threadURN={threadURN}
          channelID={channelID}
          currentBranchId={currentBranchId}
          onBranch={handleBranch}
          onForward={() => setShowForward(true)}
        />
      </div>
      <MessageBody text={text} />
      {isPondered && <PonderedBody />}
      {showForward && (
        <ForwardPopover
          ev={ev}
          threadURN={threadURN}
          channelID={channelID}
          chatTo={author}
          onClose={() => setShowForward(false)}
        />
      )}
    </div>
  );

  if (role !== 'user') {
    return (
      <div className="v2-row-with-avatar">
        <Avatar name={author} size={28} />
        <div className="v2-row-bubble">{bubble}</div>
      </div>
    );
  }

  return bubble;
}

export function V2Row({ ev, events, onResolved, threadURN, channelID, currentBranchId }) {
  const t = eventType(ev);

  if (isToolCall(t)) {
    const id = ev?.tool_call_id || (ev?.body && ev.body.tool_call_id) || '';
    return (
      <div data-testid="v2-row-tool">
        <ToolCallRow callEv={ev} resultEv={findResultFor(events, id)} compact />
      </div>
    );
  }

  if (isToolResult(t)) {
    const id = ev?.tool_call_id || (ev?.body && ev.body.tool_call_id) || '';
    if (hasCallFor(events, id)) return null;
    return (
      <div data-testid="v2-row-tool">
        <ToolCallRow orphanResult={ev} compact />
      </div>
    );
  }

  if (isApprovalRequested(t)) {
    const rid = approvalRequestID(ev);
    return (
      <div data-testid="v2-row-approval">
        <ApprovalPrompt request={ev} resolved={findResolvedFor(events, rid)} onResolved={onResolved} />
      </div>
    );
  }

  if (isApprovalResolved(t)) {
    const rid = approvalRequestID(ev);
    const hasRequest = Array.isArray(events) && events.some((e) => {
      const et = eventType(e);
      return isApprovalRequested(et) && approvalRequestID(e) === rid;
    });
    if (hasRequest) return null;
    return (
      <div data-testid="v2-row-approval">
        <ApprovalPrompt request={null} resolved={ev} />
      </div>
    );
  }

  if (t === 'assistant.message.streaming') {
    const hasText = !!(ev?.text);
    if (!hasText) {
      return <div className="v2-typing-row" data-testid="v2-row-streaming"><TypingDots visible /></div>;
    }
    return (
      <div className="v2-row v2-row-streaming" data-testid="v2-row-streaming">
        <MessageBody text={ev.text} streaming />
      </div>
    );
  }

  if (t === 'message.created' || t === 'message') {
    const body = ev?.body || ev?.payload || {};
    const text = (typeof body.content === 'string' && body.content)
      || (typeof body.text === 'string' && body.text) || '';
    const author = body.author || body.from || body.role || ev?.actor_id || '?';
    const role = (author === 'user' || author === 'human') ? 'user' : 'agent';
    const msgID = ev?.event_id || ev?.body?.client_msg_id || '';
    return (
      <MessageRow
        ev={ev} text={text} role={role} msgID={msgID}
        threadURN={threadURN} channelID={channelID} currentBranchId={currentBranchId}
      />
    );
  }

  if (t.startsWith('archivist.')) {
    return (
      <div className="v2-archivist-marker" data-testid="v2-row">
        {t}
      </div>
    );
  }

  if (t === 'context.assembled') {
    return null;
  }

  return (
    <div className="v2-unknown-event" data-testid="v2-row">
      <code>{JSON.stringify(ev)}</code>
    </div>
  );
}
